import React from 'react';
import PropTypes from 'prop-types';

import {oeInterfaceManager} from '../../../react-oe/oe-interface';
import OEInterfaceAdapter from '../../../react-oe/oe-interface-adapter';
import {OEColorPickerNumericInputType} from '../../color-picker/oe-color-picker';
import {OEGroupControl, OEControl, OEColorPickerButtonControl} from '../../elements/controls/oe-controls';
import OEPopover from '../../views/oe-popover';
import retardUpdate from '../../../lib/react/update-retarder';
import {OEUserObjectChangeFlags, OEUserObjectGeometryType, OEUserObjectToolbox, OEUserObjectParameterModels} from './oe-user-object-model';
import OEObjectModelValueEdit from '../../elements/object-model-value-edit/oe-object-model-value-edit';

export class OEUserObjectMaterialController extends React.PureComponent {

    constructor(props)  {
        super(props);

        this.oe = oeInterfaceManager.getInterface(this.props.moduleId);

        this.id = typeof(this.props.id) === 'number' ? this.props.id : null;

        this.material = null;

        this.state = this.defaultState();
        
        this.updateLanguage = this.updateLanguage.bind(this);
        this.onUserObjectChanged = this.onUserObjectChanged.bind(this);
        this.onUIControllerStateChanged = this.onUIControllerStateChanged.bind(this);

        this.onMaterialChange = this.onMaterialChange.bind(this);
        this.onColorButtonChanged = this.onColorButtonChanged.bind(this);
        this.onMaterialsChange = this.onMaterialsChange.bind(this);
    }

    defaultState()  {
        return {
            uiEnabled: false,
            defaultColors: [{x: 1, y: 1, z: 1}, {x: 1, y: 1, z: 1}],
            geometryType: OEUserObjectGeometryType.default,
            material: this.material,
            strings: {
                materialLabel: '',
                colorBtnLabel: ''
            }
        };
    }

    componentWillReceiveProps(nextProps) {
        if(nextProps.id !== this.props.id)     {
            this.id = typeof(nextProps.id) === 'number' ? nextProps.id : null;
            this.updateUserObjectState(true);
        }
    }

    onConnect()  {
        this.oe.sharedNotificationCenter.register(this.oe.NotificationName.languageChanged, this.updateLanguage);
        this.oe.sharedNotificationCenter.register(this.oe.NotificationName.userObjectChanged, this.onUserObjectChanged);
        this.oe.sharedNotificationCenter.register(this.oe.NotificationName.uiControllerStateChanged, this.onUIControllerStateChanged);
    }

    onRelease()    {
        this.oe.sharedNotificationCenter.unregister(this.oe.NotificationName.languageChanged, this.updateLanguage);
        this.oe.sharedNotificationCenter.unregister(this.oe.NotificationName.userObjectChanged, this.onUserObjectChanged);
        this.oe.sharedNotificationCenter.unregister(this.oe.NotificationName.uiControllerStateChanged, this.onUIControllerStateChanged);	
    }

    updateLanguage()   {
        this.setState({
            strings: {
                materialLabel: this.oe.sharedInterface.getLocalizedStringEnc('user_object_view_material'),
                colorBtnLabel: this.oe.sharedInterface.getLocalizedStringEnc('user_object_view_color_btn')
            }
        });
    }

    updateUserObjectState(defColor)  {
        if(!this.oe.isReady() || this.id === null)    return;
        let controller = this.oe.sharedInterface.getUIControllerUserObject();

        let data = controller.getObjectData(this.id);

        this.material = data.material;

        this.setState({
            geometryType: data.geometry.type.value,
            material: data.material
        });

        if(defColor)    this.setState({defaultColors: data.material.materials.map(mat => mat.diffuseColor)});
    }

    updateUIState()    {
        this.setState({
            uiEnabled: this.oe.sharedInterface.getUIControllerUserObject().getUIEnabled()
        });
    }

    updateState(released)   {
        if(!this.oe.isReady() || released)   {
            this.material = null;
            this.setState(this.defaultState());
            return;
        }

        retardUpdate(this, () => {
            this.updateLanguage();
            this.updateUIState();
            this.updateUserObjectState(true);
        });
    }
    
    onUserObjectChanged(message, userInfo)   {
        if(userInfo.ID !== this.id || !(userInfo.changes & (OEUserObjectChangeFlags.geometry | OEUserObjectChangeFlags.material)))  return;
        this.updateUserObjectState();
    }

    onUIControllerStateChanged(message, userInfo)    {
        if(userInfo.type === this.oe.Module.UIControllerType.user_object) {
            this.updateUIState();
        }
    }

    render()    {
        let materialControls = [];

        if(this.state.material) {
            materialControls.push(
                <OEGroupControl key={0}>
                    <OEControl>
                        <OEObjectModelValueEdit
                            className="multi-column"
                            model={OEUserObjectParameterModels.combinedMaterial}
                            object={this.state.material}
                            onChange={this.onMaterialChange}
                        />
                    </OEControl>
                </OEGroupControl>
            );
        }

        let numMaterials = Math.min(this.state.material ? this.state.material.materials.length : 0, OEUserObjectToolbox.materialCount(this.state.geometryType));
        for(let i = 0; i < numMaterials; ++i)   {
            materialControls.push(
                <OEGroupControl key={i + 1} title={this.state.strings.materialLabel + ' ' + i.toString()}>

                    <OEColorPickerButtonControl
                        moduleId={this.props.moduleId}
                        popoverClassName="with-backdrop"
                        boundariesElement={this.props.boundariesElement}
                        target={this.props.popoverTargetId}
                        placement="left-start"
                        hideArrow={true}
                        noHeader={true}
                        backdrop={false}
                        backdropOptions={{color: 'rgba(0, 0, 0, 0)'}}
                        title={this.state.strings.colorBtnLabel}
                        color={this.state.material.materials[i].diffuseColor}
                        defaultColor={this.state.defaultColors[i]}
                        hasAlphaControl={false}
                        numericInput={this.props.devFeatures ? OEColorPickerNumericInputType.textField : OEColorPickerNumericInputType.rgbInput}
                        hasPaletteButton={this.props.devFeatures}
                        onChange={this.onColorButtonChanged}
                        index={i}
                    />

                    <OEControl>
                        <OEObjectModelValueEdit
                            className="multi-column"
                            model={OEUserObjectParameterModels.material}
                            object={this.state.material.materials[i]}
                            onChange={this.onMaterialsChange}
                            index={i}
                        />
                    </OEControl>
                </OEGroupControl>
            );
        }

        return (
            <React.Fragment>
                <OEInterfaceAdapter moduleId={this.props.moduleId} receiver={this}/>
                <div className={'user-object-material-controller ' + this.props.className}>
                    {materialControls}
                </div>
            </React.Fragment>
        );
    }

    onMaterialChange(sender, params) {
        if(!this.oe.isReady() || this.id === null || this.material === null) return;

        let material = {
            alpha: params.alpha,
            materials: this.material.materials
        };

        this.oe.sharedInterface.getUIControllerUserObject().setObjectCombinedMaterial(this.id, material);
    }

    onColorButtonChanged(sender, color) {
        if(!this.oe.isReady() || this.id === null) return;
        this.oe.sharedInterface.getUIControllerUserObject().setObjectColor(this.id, sender.props.index, color);
    }

    onMaterialsChange(sender, params) {
        if(!this.oe.isReady() || this.id === null) return;

        let material = {
            diffuseColor: this.material.materials[sender.props.index].diffuseColor,
            diffuse: params.diffuse,
            ambient: params.ambient,
            specBrightness: params.specBrightness,
            roughness: params.roughness,
        };

        this.oe.sharedInterface.getUIControllerUserObject().setObjectMaterial(this.id, sender.props.index, material);
    }
}

OEUserObjectMaterialController.defaultProps = {
    moduleId: '',
    className: ''
};

OEUserObjectMaterialController.propTypes = {
    moduleId: PropTypes.string,
    className: PropTypes.string,
    id: PropTypes.number
}; 

export const OEUserObjectMaterialPopoverController = OEPopover.coat(OEUserObjectMaterialController, {
    noHeader: true,
}, {
    placement: 'right',
    buttonClassName: 'transparent-btn',
    controllerClassName: 'user-object-material-popover compact'
}, {
    controllerClassName: PropTypes.string
});